import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
} from '@mui/material';
import { motion } from 'framer-motion';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import MessageIcon from '@mui/icons-material/Message';

const Contact: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitting(false);
    setShowSuccess(true);
    setFormData({ name: '', email: '', message: '' });
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  return (
    <Box
      component="section"
      id="contact"
      sx={{
        py: { xs: 4, md: 6 },
        bgcolor: 'background.paper',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(0, 255, 136, 0.02) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
        >
          {/* Header */}
          <motion.div variants={itemVariants}>
            <Typography
              variant={isMobile ? 'h4' : 'h2'}
              component="h2"
              sx={{
                fontWeight: 400,
                mb: { xs: 1, md: 1 },
                color: 'text.primary',
                textAlign: 'center',
                fontFamily: '"Charter", "Georgia", serif',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Get in Touch
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                mb: { xs: 3, md: 4 },
                textAlign: 'center',
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.7,
                fontFamily: '"Kievit", "Roboto", sans-serif',
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              Ready to transform your learning experience? Have questions about our AI-powered platform? 
              We'd love to hear from you.
            </Typography>
          </motion.div>

          {/* Contact Form */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}05 0%, ${theme.palette.secondary.main}05 100%)`,
                border: `1px solid ${theme.palette.primary.main}20`,
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}03 0%, transparent 50%)`,
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 1,
                },
              }}
            >
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {/* Name and Email Row */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  {/* Name Field */}
                  <TextField
                    required
                    label="Name"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <PersonIcon 
                          sx={{ 
                            mr: 1, 
                            color: theme.palette.primary.main,
                            fontSize: 18,
                          }} 
                        />
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${theme.palette.divider}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                        },
                        '&.Mui-focused': {
                          borderColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.main}30`,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'text.secondary',
                        fontFamily: '"Kievit", "Roboto", sans-serif',
                        fontSize: '0.875rem',
                      },
                      '& .MuiInputBase-input': {
                        fontFamily: '"Kievit", "Roboto", sans-serif',
                        fontSize: '0.875rem',
                      },
                    }}
                  />

                  {/* Email Field */}
                  <TextField
                    required
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <EmailIcon 
                          sx={{ 
                            mr: 1, 
                            color: theme.palette.primary.main,
                            fontSize: 18,
                          }} 
                        />
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${theme.palette.divider}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                        },
                        '&.Mui-focused': {
                          borderColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.main}30`,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'text.secondary',
                        fontFamily: '"Kievit", "Roboto", sans-serif',
                        fontSize: '0.875rem',
                      },
                      '& .MuiInputBase-input': {
                        fontFamily: '"Kievit", "Roboto", sans-serif',
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Box>

                {/* Message Field */}
                <TextField
                  required
                  fullWidth
                  label="Message"
                  multiline
                  rows={3}
                  value={formData.message}
                  onChange={handleInputChange('message')}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <MessageIcon 
                        sx={{ 
                          mr: 1, 
                          mt: 1,
                          color: theme.palette.primary.main,
                          fontSize: 18,
                        }} 
                      />
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: `1px solid ${theme.palette.divider}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
                      },
                      '&.Mui-focused': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 0 0 2px ${theme.palette.primary.main}30`,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'text.secondary',
                      fontFamily: '"Kievit", "Roboto", sans-serif',
                      fontSize: '0.875rem',
                    },
                    '& .MuiInputBase-input': {
                      fontFamily: '"Kievit", "Roboto", sans-serif',
                      fontSize: '0.875rem',
                    },
                  }}
                />

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="contained"
                  size="medium"
                  disabled={isSubmitting}
                  startIcon={<SendIcon />}
                  sx={{
                    mt: 1,
                    py: 1,
                    px: 3,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 4px 16px ${theme.palette.primary.main}25`,
                    fontFamily: '"Kievit", "Roboto", sans-serif',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 20px ${theme.palette.primary.main}35`,
                    },
                    '&:disabled': {
                      opacity: 0.7,
                      transform: 'none',
                    },
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </Box>
            </Paper>
          </motion.div>
        </motion.div>
      </Container>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{
            borderRadius: 2,
            fontFamily: '"Kievit", "Roboto", sans-serif',
          }}
        >
          Message sent successfully! We'll get back to you soon.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact; 